import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import moment from "moment";

import AppForm from "components/Shared/AppForm";
import RichTextEditor from "components/Shared/RichTextEditor";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import FileDropzone from "components/Shared/FileDropzone"; 

import { useLazyListListQuery } from "store/services/listService";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useLazyGetTeamDDLQuery } from "store/services/teamApi";
import { useAuth, useDOUploadFile } from "hooks";

import {
  RESPONSIBLE_SOURCES,
  TASK_PRIORITIES,
} from "data/static/staticOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const INITIAL_STATE = {
  title: "",
  category: "",
  stage: "",
  priority: "Normal",
  responsibleSource: "User",
  responsible: "",
  dueDate: moment().format("YYYY-MM-DD"),
  isActive: true,
  description: "",
  attachments: [],
};

const TaskForm = ({
  taskDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const [responsibleSource, setResponsibleSource] = React.useState("User");
  const { handleUploadFiles } = useDOUploadFile();
  const { viewEntity } = useAuth();

  const navigate = useNavigate();

  const [
    fetchTaskStages,
    {
      data: taskStages = [],
      isLoading: loadingStages,
      isFetching: fetchingStages,
    },
  ] = useLazyListListQuery();

  const [
    fetchTaskCategories,
    {
      data: taskCategories = [],
      isLoading: loadingCategories,
      isFetching: fetchingCategories,
    },
  ] = useLazyListListQuery();

  const [
    fetchTeams,
    { data: teamList = [], isLoading: loadingTeams, isFetching: fetchingTeams },
  ] = useLazyGetTeamDDLQuery();

  const [
    fetchUsers,
    { data: userList = [], isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const loading =
    isBusy ||
    loadingUsers ||
    fetchingUsers ||
    loadingTeams ||
    fetchingTeams ||
    loadingStages ||
    fetchingStages ||
    loadingCategories ||
    fetchingCategories;

  const _mappedResponsibleUserList = React.useMemo(() => {
    switch (responsibleSource) {
      case "User":
        return userList?.map((user) => ({
          label: user?.designation
            ? `${user?.name} | ${user?.designation}`
            : user?.name,
          id: user._id,
        }));

      case "Team":
        return teamList?.map((team) => ({
          label: team?.name,
          id: team?._id,
        }));

      default:
        return [];
    }
  }, [responsibleSource, teamList, userList]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  const getSelectedResponsibleOption = React.useCallback(
    (responsible) => {
      if (!responsible) return;

      switch (taskDetail?.responsibleSource) {
        case "User":
        case "Team":
          return {
            label: responsible?.designation
              ? `${responsible?.name} | ${responsible?.designation}`
              : responsible?.name,
            id: responsible?._id,
          };

        default:
          return "";
      }
    },
    [taskDetail?.responsibleSource]
  );

  const handleOnTaskSubmit = async (data) => {
    const _payload = {
      ...data,
      responsibleSource,
      responsible: data?.responsible?.id,
    };

    // Handle attachments
    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "TASK_DOCUMENT",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      _payload.attachments = [...existingAttachments, ...uploadedFiles];
    } else {
      _payload.attachments = existingAttachments;
    }

    onSubmit(_payload);
  };

  const handleResponsibleSourceChange = (e) => {
    setResponsibleSource(e.target.value);
    setValue("responsible", null);
  };

  const handleLazyFetch = React.useCallback(
    async (entity) => {
      fetchTaskStages({
        type: ENTITY_LIST_CONSTANTS.TASK_STAGES,
        isSubType: false,
        includeChildren: true,
        status: "Active",
        ...(entity && { entity }),
      });

      fetchTaskCategories({
        type: ENTITY_LIST_CONSTANTS.TASK_CATEGORIES,
        isSubType: false,
        includeChildren: true,
        status: "Active",
        ...(entity && { entity }),
      });

      fetchUsers({ disableEntityFilter: true });

      fetchTeams({ ...(entity && { entity }) });
    },
    [fetchTaskStages, fetchTaskCategories, fetchUsers, fetchTeams]
  );

  // Function to generate valid dates (Sunday to Friday) within a week
  const getValidWeekdays = () => {
    const dates = [];
    for (let i = 0; i <= 7; i++) {
      const date = moment().add(i, "days");
      if (date.isoWeekday() <= 6 || date.isoWeekday() === 7) {
        dates.push(date.format("YYYY-MM-DD"));
      }
    }
    return dates;
  };

  // Set default stage to "Open" when taskStages are loaded
  React.useEffect(() => {
    if (taskStages.length > 0 && !taskDetail) {
      const openStage = taskStages.find((stage) => stage.name === "Open");
      if (openStage) {
        setValue("stage", openStage._id);
      }
    }
  }, [taskStages, setValue, taskDetail]);

  React.useEffect(() => {
    if (!taskDetail) return;

    setResponsibleSource(taskDetail?.responsibleSource ?? "User");

    const selectableResponsible = getSelectedResponsibleOption(
      taskDetail?.responsible
    );

    reset({
      ...taskDetail,
      category: taskDetail?.category?._id ?? "",
      stage: taskDetail?.stage?._id ?? "",
      dueDate: moment(taskDetail?.dueDate).format("YYYY-MM-DD"),
      responsible: selectableResponsible,
      attachments: taskDetail?.attachments ?? [], // Include attachments
    });

    return () => {
      reset(INITIAL_STATE);
    };
  }, [getSelectedResponsibleOption, reset, taskDetail]);

  React.useEffect(() => {
    handleLazyFetch();
  }, [handleLazyFetch]);

  return (
    <AppForm loading={loading}>
      <AppForm.Content>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <Controller
              name="title"
              control={control}
              rules={{ required: "Please provide a Title." }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  required
                  label="Title"
                  type="text"
                  size="small"
                  {...field}
                />
              )}
            />
            {!!errors.title && (
              <FormHelperText error>{errors.title.message}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Please select a Category." }}
              render={({ field }) => (
                <FormControl fullWidth required size="small">
                  <InputLabel id="select-category">Category</InputLabel>
                  <Select
                    fullWidth
                    labelId="select-category"
                    label="Category"
                    placeholder="Select Category"
                    id="role"
                    value={field?.value ?? ""}
                    {...field}
                  >
                    {taskCategories?.map((category) => (
                      <MenuItem key={category?._id} value={category?._id}>
                        {category?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {!!errors.category && (
              <FormHelperText error>{errors.category.message}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="priority"
              control={control}
              rules={{ required: "Please select a Priority." }}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-priority">Priority</InputLabel>
                  <Select
                    fullWidth
                    labelId="select-priority"
                    label="Priority"
                    placeholder="Select Priority"
                    id="role"
                    value={field.value ?? ""}
                    {...field}
                  >
                    {TASK_PRIORITIES?.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {!!errors.priority && (
              <FormHelperText error>{errors.priority.message}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="stage"
              control={control}
              rules={{ required: "Please select a Stage." }}
              render={({ field }) => (
                <FormControl fullWidth required size="small">
                  <InputLabel id="select-stage">Stage</InputLabel>
                  <Select
                    fullWidth
                    labelId="select-stage"
                    label="Stage"
                    placeholder="Select Stage"
                    value={field?.value ?? ""}
                    {...field}
                  >
                    {taskStages?.map((stage) => (
                      <MenuItem key={stage?._id} value={stage?._id}>
                        {stage?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {!!errors.stage && (
              <FormHelperText error>{errors.stage.message}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-responsible-type">
                Responsible Type
              </InputLabel>
              <Select
                fullWidth
                labelId="select-responsible-type"
                label="Responsible Type"
                placeholder="Select Responsible Type"
                id="role"
                value={responsibleSource}
                onChange={handleResponsibleSourceChange}
              >
                {RESPONSIBLE_SOURCES?.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="responsible"
              control={control}
              render={({ field }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Responsible"
                  options={_mappedResponsibleUserList}
                  value={field?.value}
                  onChange={(_, data) => setValue("responsible", data)}
                  loading={loadingTeams || loadingUsers}
                  loadingText="Loading Responsible Users..."
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="dueDate"
              control={control}
              rules={{
                required: "Please select a Due Date.",
                validate: (value) => {
                  const selectedDate = moment(value);
                  const today = moment();
                  const maxDate = moment().add(7, "days");
                  if (selectedDate.isBefore(today, "day")) {
                    return "Due date cannot be in the past";
                  }
                  if (selectedDate.isAfter(maxDate, "day")) {
                    return "Due date cannot be more than a week from today";
                  }
                  if (selectedDate.isoWeekday() !== 7 && selectedDate.isoWeekday() > 6) {
                    return "Due date must be Sunday to Friday";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-due-date">Due Date</InputLabel>
                  <Select
                    labelId="select-due-date"
                    label="Due Date"
                    value={field.value}
                    {...field}
                  >
                    {getValidWeekdays().map((date) => (
                      <MenuItem key={date} value={date}>
                        {moment(date).format("MMMM D, YYYY (dddd)")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {!!errors.dueDate && (
              <FormHelperText error>{errors.dueDate.message}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      value={field?.value}
                      {...field}
                    />
                  }
                  label="Is Active?"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormHelperText>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormHelperText>Description*</FormHelperText>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Please provide a Description." }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    taskDetail?.description
                      ? taskDetail.description
                      : "<p>Enter Task Description.</p>"
                  }
                  onChange={onChange}
                />
              )}
            />
            {!!errors.description && (
              <FormHelperText error>
                {errors.description.message}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnTaskSubmit)}>
          {taskDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default TaskForm;